import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Paper,
  Stack,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconEdit, IconPlus, IconRestore, IconTag, IconTrash } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/api/queryKeys';
import { useEinrichtung } from '@/api/useEinrichtung';
import { useKatalogansicht, useSetSachkontoLabel } from '@/api/useSachkonten';
import { usePlan } from '@/api/usePlaene';
import {
  useCreateSachkosten,
  useDeleteSachkosten,
  useSachkosten,
  useUpdateSachkosten,
} from '@/api/useSachkosten';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ErrorState } from '@/components/ErrorState';
import { LoadingState } from '@/components/LoadingState';
import { MoneyInput } from '@/components/MoneyInput';
import type { KatalogZeile, SachkostenRow, UUID } from '@/api/types';
import { SachkostenEditDialog } from './SachkostenEditDialog';
import { SachkostenUmbenennenDialog } from './SachkostenUmbenennenDialog';
import { describeApiError, formatEuro } from './utils';

/**
 * Punkte, die eingerueckt unter "Regulaere Kosten" stehen (Migration 0027).
 *
 * Fachlich gehoeren sie zu den laufenden, regelmaessigen Kosten — im
 * Gegensatz zu "Besondere Kosten". Sie haben trotzdem eigene Betragsfelder
 * und zaehlen einzeln in die Bereichssumme; die Einrueckung ordnet sie nur
 * optisch zu, sie macht "Regulaere Kosten" NICHT zur Summenzeile darueber.
 *
 * Die Zuordnung steht hier und nicht im Katalog, weil sie reine Darstellung
 * ist — der Katalog (``ref_sachkonto``) kennt keine Hierarchie. Aendern sich
 * die Codes in Migration 0027, greift die Einrueckung stumm nicht mehr;
 * dagegen sichert ``SachkostenTab.test.tsx``.
 */
const EINGERUECKT_UNTER_REGULAER = new Set(['verpflegung', 'miete', 'mietnebenkosten']);

interface KostenBereichProps {
  planId: UUID;
  disabled: boolean;
  onMutated: () => void;
  /** "sach" oder "personal" — welcher Bereich der Katalogansicht gezeigt wird. */
  bereichCode: string;
  /** Ueberschrift des Kastens. */
  titel: string;
  /** Beschriftung der Summenzeile. */
  summeLabel: string;
  /**
   * Gesetzt: Der Kasten wird in einen grauen Themenblock mit dieser
   * Kleinschrift-Ueberschrift gefasst — dasselbe Muster wie die Bloecke
   * "Gruppen" und "Schwerpunktkita-Pauschale" im Belegung-Reiter. Gedacht
   * fuer den Personalkosten-Reiter, wo der Kostenblock zwischen den
   * Stellenlisten sonst untergeht. Ohne die Prop steht der Kasten fuer
   * sich (Sachkosten-Reiter, wo er ohnehin allein steht).
   */
  themenblockLabel?: string;
}

/**
 * Ein Kostenbereich der schlanken Ansicht (Migration 0027).
 *
 * Dieselbe Komponente traegt beide Bereiche: "Sachkosten" (fuenf Punkte) im
 * Sachkosten-Reiter und "Personalkosten" (zwei Punkte) unterhalb der
 * Stellenliste im Personalkosten-Reiter. Beide holen dieselbe
 * Katalogansicht und zeigen daraus ihren Bereich — der Endpunkt liefert
 * ohnehin beide, und so kann die Anzeige der beiden Bereiche nicht
 * auseinanderlaufen.
 *
 * Aufbau je Punkt: eine Zeile mit Bezeichnung und Jahresbetrag. Die beiden
 * Punkte "Besondere Kosten" (``sammelpunkt``) tragen zusaetzlich einen
 * Knopf "Position hinzufuegen" und darunter ihre eigenen Positionen.
 * Solange dort keine eigene Position existiert, ist das Betragsfeld des
 * Punktes selbst bedienbar — man kann also eine Gesamtsumme eintragen,
 * statt einzeln aufzuschluesseln.
 */
export function KostenBereich({
  planId,
  disabled,
  onMutated,
  bereichCode,
  titel,
  summeLabel,
  themenblockLabel,
}: KostenBereichProps) {
  const { t, i18n } = useTranslation(['sachkosten', 'common', 'errors']);
  const qc = useQueryClient();

  const katalog = useKatalogansicht(planId);
  // Volle Positionsdaten fuer den Bearbeiten-Dialog — die Katalogansicht
  // liefert nur Betrag und Bezeichnung.
  const sachRows = useSachkosten(planId);
  const createSach = useCreateSachkosten(planId);
  const updateSach = useUpdateSachkosten(planId);
  const deleteSach = useDeleteSachkosten(planId);

  // Fuer die traegerweite Umbenennung ("dauerhaft") wird der Traeger
  // gebraucht — der Plan kennt nur seine Einrichtung.
  const planQuery = usePlan(planId);
  const einrichtungQuery = useEinrichtung(planQuery.data?.einrichtung_id ?? null);
  const traegerId: UUID | null = einrichtungQuery.data?.traeger_id ?? null;
  const setLabel = useSetSachkontoLabel(traegerId);

  const [createUnter, setCreateUnter] = useState<string | null>(null);
  const [editZeile, setEditZeile] = useState<SachkostenRow | null>(null);
  const [editZeileBezeichnung, setEditZeileBezeichnung] = useState<string | null>(null);
  const [deleteZeile, setDeleteZeile] = useState<KatalogZeile | null>(null);
  const [umbenennenZeile, setUmbenennenZeile] = useState<KatalogZeile | null>(null);

  const rowById = new Map((sachRows.data ?? []).map((r) => [r.id, r]));
  const bereich = katalog.data?.gruppen.find((g) => g.code === bereichCode) ?? null;

  const handleError = (err: unknown) =>
    notifications.show({
      color: 'red',
      title: t('errors:generic'),
      message: describeApiError(err) || t('errors:generic'),
    });

  const nachSpeichern = async () => {
    await qc.invalidateQueries({ queryKey: queryKeys.sach.katalogansicht(planId) });
    onMutated();
  };

  const speichereBetrag = async (zeile: KatalogZeile, wert: number) => {
    try {
      if (zeile.position_id) {
        await updateSach.mutateAsync({ id: zeile.position_id, patch: { betrag_jahr: wert } });
      } else if (zeile.konto_code) {
        await createSach.mutateAsync({
          art: zeile.bezeichnung,
          konto_code: zeile.konto_code,
          betrag_jahr: wert,
          zeitliche_verteilung: {},
        });
      }
      await nachSpeichern();
    } catch (err) {
      handleError(err);
    }
  };

  const umbenennenNurPlan = async (zeile: KatalogZeile, bezeichnung: string) => {
    try {
      if (zeile.position_id) {
        await updateSach.mutateAsync({
          id: zeile.position_id,
          patch: { bezeichnung_override: bezeichnung },
        });
      } else if (zeile.konto_code) {
        // Punkt ohne Position — die Position entsteht erst durch die erste
        // Eingabe, hier mit 0 EUR, weil nur der Name gemeint ist.
        await createSach.mutateAsync({
          art: zeile.bezeichnung,
          konto_code: zeile.konto_code,
          betrag_jahr: 0,
          zeitliche_verteilung: {},
          bezeichnung_override: bezeichnung,
        });
      }
      setUmbenennenZeile(null);
      notifications.show({ color: 'teal', message: t('sachkosten:info.umbenannt') });
      await nachSpeichern();
    } catch (err) {
      handleError(err);
    }
  };

  const umbenennenDauerhaft = async (zeile: KatalogZeile, bezeichnung: string) => {
    if (!zeile.konto_code || !traegerId) {
      notifications.show({
        color: 'red',
        title: t('errors:generic'),
        message: t('sachkosten:umbenennen.traegerUnbekannt'),
      });
      return;
    }

    // Reihenfolge ist die fachliche Kernregel: erst die Traeger-Umbenennung
    // schreiben, danach — falls vorhanden — die Plan-Ausnahme entfernen.
    // Andersherum saehe der Nutzer seine eigene Aenderung nicht, weil die
    // Plan-Ausnahme in der Aufloesung vor der Traeger-Ebene steht.
    try {
      await setLabel.mutateAsync({ kontoCode: zeile.konto_code, bezeichnung });
    } catch (err) {
      handleError(err);
      return;
    }

    setUmbenennenZeile(null);

    if (zeile.herkunft === 'plan' && zeile.position_id) {
      try {
        await updateSach.mutateAsync({
          id: zeile.position_id,
          patch: { bezeichnung_override: null },
        });
        notifications.show({ color: 'teal', message: t('sachkosten:info.umbenannt') });
      } catch {
        notifications.show({
          color: 'yellow',
          title: t('sachkosten:umbenennen.title'),
          message: t('sachkosten:umbenennen.teilweiseGespeichert', {
            bezeichnung: zeile.bezeichnung,
          }),
        });
      }
    } else {
      notifications.show({ color: 'teal', message: t('sachkosten:info.umbenannt') });
    }

    await nachSpeichern();
  };

  const zuruecksetzen = async (zeile: KatalogZeile) => {
    if (!zeile.position_id) return;
    try {
      await updateSach.mutateAsync({
        id: zeile.position_id,
        patch: { bezeichnung_override: null },
      });
      await nachSpeichern();
    } catch (err) {
      handleError(err);
    }
  };

  const oeffneBearbeiten = (zeile: KatalogZeile) => {
    const row = zeile.position_id ? rowById.get(zeile.position_id) : undefined;
    if (row) {
      setEditZeile(row);
      setEditZeileBezeichnung(zeile.bezeichnung);
    }
  };

  const kasten = (
    <Paper withBorder p="md" data-testid={`kosten-bereich-${bereichCode}`}>
      {/* Im Themenblock traegt die graue Aussenbox bereits die
          Ueberschrift — sie hier zu wiederholen hiesse dasselbe zweimal. */}
      {themenblockLabel ? null : (
        <Title order={4} mb="sm">
          {titel}
        </Title>
      )}
      {katalog.isLoading ? <LoadingState height={120} /> : null}
      {katalog.isError ? <ErrorState error={katalog.error} /> : null}
      {bereich ? (
        <Stack gap="xs">
          {bereich.zeilen.map((zeile) => (
            <PunktZeile
              key={zeile.konto_code ?? String(zeile.position_id)}
              zeile={zeile}
              rowById={rowById}
              disabled={disabled}
              onSave={speichereBetrag}
              onEdit={oeffneBearbeiten}
              onDeleteRequest={setDeleteZeile}
              onRename={setUmbenennenZeile}
              onZuruecksetzen={zuruecksetzen}
              eingerueckt={
                !!zeile.konto_code && EINGERUECKT_UNTER_REGULAER.has(zeile.konto_code)
              }
              onAddPosition={
                zeile.sammelpunkt && zeile.konto_code
                  ? () => setCreateUnter(zeile.konto_code)
                  : undefined
              }
            />
          ))}
          <Group
            justify="space-between"
            pt="xs"
            style={{ borderTop: '1px solid var(--mantine-color-gray-4)' }}
          >
            <Text fw={700}>{summeLabel}</Text>
            <Text fw={700} data-testid={`kosten-summe-${bereichCode}`}>
              {formatEuro(Number(bereich.summe_jahr), i18n.language)}
            </Text>
          </Group>
        </Stack>
      ) : null}
    </Paper>
  );

  return (
    <Stack>
      {themenblockLabel ? (
        <Paper
          withBorder
          p="md"
          bg="var(--mantine-color-gray-0)"
          style={{ borderColor: 'var(--mantine-color-gray-3)' }}
        >
          <Text size="xs" tt="uppercase" fw={700} c="dimmed" mb="sm" style={{ letterSpacing: 1 }}>
            {themenblockLabel}
          </Text>
          {kasten}
        </Paper>
      ) : (
        kasten
      )}

      <SachkostenEditDialog
        opened={createUnter !== null}
        planId={planId}
        position={null}
        bereichCode={createUnter}
        onClose={() => setCreateUnter(null)}
        onSaved={() => {
          void nachSpeichern();
        }}
      />

      <SachkostenEditDialog
        opened={editZeile !== null}
        planId={planId}
        position={editZeile}
        katalogBezeichnung={editZeileBezeichnung}
        onClose={() => {
          setEditZeile(null);
          setEditZeileBezeichnung(null);
        }}
        onSaved={() => {
          void nachSpeichern();
        }}
      />

      {deleteZeile ? (
        <ConfirmDialog
          opened
          title={t('sachkosten:position.remove')}
          message={t('sachkosten:delete.message')}
          confirmLabel={t('common:actions.delete')}
          danger
          onCancel={() => setDeleteZeile(null)}
          onConfirm={async () => {
            const target = deleteZeile;
            setDeleteZeile(null);
            if (!target?.position_id) return;
            try {
              await deleteSach.mutateAsync(target.position_id);
              notifications.show({ color: 'teal', message: t('sachkosten:info.deleted') });
              await nachSpeichern();
            } catch (err) {
              handleError(err);
            }
          }}
        />
      ) : null}

      {umbenennenZeile ? (
        <SachkostenUmbenennenDialog
          opened
          kontoCode={umbenennenZeile.konto_code ?? ''}
          aktuell={umbenennenZeile.bezeichnung}
          onNurPlan={(bezeichnung) => {
            void umbenennenNurPlan(umbenennenZeile, bezeichnung);
          }}
          onDauerhaft={(bezeichnung) => {
            void umbenennenDauerhaft(umbenennenZeile, bezeichnung);
          }}
          onClose={() => setUmbenennenZeile(null)}
        />
      ) : null}
    </Stack>
  );
}

interface PunktZeileProps {
  zeile: KatalogZeile;
  rowById: Map<UUID, SachkostenRow>;
  disabled: boolean;
  onSave: (zeile: KatalogZeile, wert: number) => void;
  onEdit: (zeile: KatalogZeile) => void;
  onDeleteRequest: (zeile: KatalogZeile) => void;
  onRename: (zeile: KatalogZeile) => void;
  onZuruecksetzen: (zeile: KatalogZeile) => void;
  /** Optisch unter "Regulaere Kosten" eingerueckt (siehe Konstante oben). */
  eingerueckt?: boolean;
  onAddPosition?: () => void;
}

/** Ein Katalog-Punkt samt seiner eigenen Positionen (falls Sammelpunkt). */
function PunktZeile({
  zeile,
  rowById,
  disabled,
  onSave,
  onEdit,
  onDeleteRequest,
  onRename,
  onZuruecksetzen,
  eingerueckt,
  onAddPosition,
}: PunktZeileProps) {
  const { t } = useTranslation(['common', 'sachkosten']);
  const eigene = zeile.eigene ?? [];
  // Sammelbetrag und Einzelpositionen schliessen sich aus: Sobald eine
  // eigene Position existiert, ist die Summe der Positionen massgeblich.
  // Das Feld des Punktes bleibt dann leer und gesperrt, statt eine zweite,
  // konkurrierende Eingabestelle anzubieten.
  const hatEigene = eigene.length > 0;
  const eigeneSumme = eigene.reduce((s, e) => s + Number(e.betrag_jahr ?? 0), 0);

  return (
    <Stack
      gap={4}
      pl={eingerueckt ? 'lg' : undefined}
      data-testid={`kosten-punkt-${zeile.konto_code}`}
    >
      <ZeileMitFeld
        zeile={zeile}
        row={zeile.position_id ? rowById.get(zeile.position_id) : undefined}
        disabled={disabled}
        gesperrt={hatEigene}
        ersatzBetrag={hatEigene ? eigeneSumme : null}
        onSave={(wert) => onSave(zeile, wert)}
        onEdit={() => onEdit(zeile)}
        onDeleteRequest={() => onDeleteRequest(zeile)}
        onRename={() => onRename(zeile)}
        onZuruecksetzen={() => onZuruecksetzen(zeile)}
        hervorgehoben={!eingerueckt}
        aktion={
          onAddPosition && !disabled ? (
            <Button leftSection={<IconPlus size={14} />} size="xs" onClick={onAddPosition}>
              {t('sachkosten:position.add')}
            </Button>
          ) : null
        }
      />
      {eigene.map((e) => (
        <Group key={String(e.position_id)} pl="lg" wrap="nowrap">
          <ZeileMitFeld
            zeile={e}
            row={e.position_id ? rowById.get(e.position_id) : undefined}
            disabled={disabled}
            gesperrt={false}
            ersatzBetrag={null}
            onSave={(wert) => onSave(e, wert)}
            onEdit={() => onEdit(e)}
            onDeleteRequest={() => onDeleteRequest(e)}
            onRename={() => onRename(e)}
            onZuruecksetzen={() => onZuruecksetzen(e)}
            hervorgehoben={false}
            aktion={null}
          />
        </Group>
      ))}
    </Stack>
  );
}

interface ZeileMitFeldProps {
  zeile: KatalogZeile;
  row: SachkostenRow | undefined;
  disabled: boolean;
  /** Feld gesperrt, weil die Summe aus Unterpositionen stammt. */
  gesperrt: boolean;
  /** Anzuzeigender Betrag, wenn das Feld gesperrt ist. */
  ersatzBetrag: number | null;
  onSave: (wert: number) => void;
  onEdit: () => void;
  onDeleteRequest: () => void;
  onRename: () => void;
  onZuruecksetzen: () => void;
  /**
   * Fett gesetzt: die Punkte auf oberster Ebene ("Regulaere Kosten",
   * "Besondere Kosten"). Die darunter eingerueckten Punkte und die eigenen
   * Positionen stehen normal — so traegt die Schriftstaerke dieselbe
   * Gliederung wie der Einzug, statt ihr zu widersprechen.
   */
  hervorgehoben?: boolean;
  aktion: React.ReactNode;
}

function ZeileMitFeld({
  zeile,
  row,
  disabled,
  gesperrt,
  ersatzBetrag,
  onSave,
  onEdit,
  onDeleteRequest,
  onRename,
  onZuruecksetzen,
  hervorgehoben,
  aktion,
}: ZeileMitFeldProps) {
  const { t, i18n } = useTranslation(['common', 'sachkosten']);
  const initial = zeile.betrag_jahr != null ? Number(zeile.betrag_jahr) : null;
  const [wert, setWert] = useState<number | null>(initial);

  useEffect(() => {
    setWert(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zeile.betrag_jahr]);

  // Umbenennen greift nur bei Katalog-Punkten — eigene, freitextige
  // Positionen haben schon ueber "Bearbeiten" ein freies Namensfeld und
  // keinen traegerweiten Standard, den man umbenennen koennte.
  const umbenennbar = zeile.herkunft !== 'eigen' && !!zeile.konto_code;

  // Erlaeuternder Beispieltext je Punkt ("Verwaltung, Reinigung, ..."). Er
  // haengt am stabilen Code, nicht an der Bezeichnung — nach einer
  // Umbenennung bleibt er deshalb stehen und erklaert weiterhin, was
  // gemeint ist. Punkte ohne hinterlegten Hinweis (Miete, Mietnebenkosten)
  // und eigene Positionen zeigen keinen.
  const hinweisKey = zeile.konto_code ? `sachkosten:punkt.hinweis.${zeile.konto_code}` : null;
  const hinweis = hinweisKey && i18n.exists(hinweisKey) ? t(hinweisKey) : null;

  // Beispieltext im leeren Betragsfeld — sagt, was dort erwartet wird
  // ("Summe/Jahr" bzw. "Gesamt oder einzelne Posten"). Hängt wie der
  // Hinweis am stabilen Code, nicht an der Bezeichnung.
  const platzhalterKey = zeile.konto_code
    ? `sachkosten:punkt.platzhalter.${zeile.konto_code}`
    : null;
  const platzhalter = platzhalterKey && i18n.exists(platzhalterKey) ? t(platzhalterKey) : undefined;

  return (
    <Group
      justify="space-between"
      wrap="nowrap"
      style={{ flex: 1 }}
      data-testid={`kosten-zeile-${zeile.konto_code ?? zeile.position_id}`}
    >
      <Group gap={6} wrap="nowrap">
        <Stack gap={0}>
          <Text size="sm" fw={hervorgehoben ? 600 : undefined}>
            {zeile.bezeichnung}
          </Text>
          {hinweis ? (
            <Text size="xs" c="dimmed">
              {hinweis}
            </Text>
          ) : null}
        </Stack>
        {zeile.herkunft === 'plan' ? (
          <Group gap={4} wrap="nowrap">
            <Badge size="xs" variant="light" color="blue">
              {t('sachkosten:umbenennen.markierung')}
            </Badge>
            {!disabled ? (
              <Tooltip label={t('sachkosten:umbenennen.zuruecksetzen')}>
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  aria-label={t('sachkosten:umbenennen.zuruecksetzen')}
                  onClick={onZuruecksetzen}
                >
                  <IconRestore size={14} />
                </ActionIcon>
              </Tooltip>
            ) : null}
          </Group>
        ) : null}
        {umbenennbar && !disabled ? (
          <Tooltip label={t('sachkosten:umbenennen.title')}>
            <ActionIcon
              variant="subtle"
              size="sm"
              aria-label={t('sachkosten:umbenennen.title')}
              onClick={onRename}
            >
              <IconTag size={14} />
            </ActionIcon>
          </Tooltip>
        ) : null}
      </Group>
      <Group gap={4} wrap="nowrap">
        {aktion}
        <MoneyInput
          aria-label={zeile.bezeichnung}
          placeholder={platzhalter}
          value={gesperrt ? ersatzBetrag : wert}
          onChange={setWert}
          onBlur={() => {
            if (gesperrt) return;
            // Ein geleertes Feld bedeutet "kostet nichts" und traegt sich
            // als 0 EUR ein. Frueher sprang es auf den letzten Wert
            // zurueck — man musste die 0 von Hand tippen, um einen Posten
            // wieder auf null zu setzen.
            //
            // Geloescht wird dadurch nichts: Die Position bleibt mit 0 EUR
            // bestehen. Das Entfernen einer eigenen Position laeuft
            // weiterhin ausschliesslich ueber den Papierkorb mit
            // Rueckfrage — genau diese Trennung war der Grund fuer das
            // frueherere Zurueckspringen.
            const ziel = wert ?? 0;
            // Noch nie etwas eingetragen UND weiterhin leer: keine
            // 0-Position anlegen, sonst fuellte schon ein Klick ins Feld
            // den Plan mit Nullzeilen.
            if (initial === null && wert === null) return;
            if (ziel !== initial) onSave(ziel);
            setWert(ziel);
          }}
          disabled={disabled || gesperrt}
          size="xs"
          w={140}
        />
        {/* Stift und Papierkorb nur bei eigenen Positionen: Ein
            Katalog-Punkt wird ueber sein Feld (Betrag) und ueber
            "Bezeichnung aendern" (Name) gepflegt — der Dialog haette dort
            nichts anzubieten, und sein Pflichtfeld "Art" ist bei
            Katalog-Punkten leer, das Speichern schluege fehl. */}
        {zeile.position_id && !zeile.konto_code && !disabled && !gesperrt ? (
          <>
            <Tooltip label={t('actions.edit')}>
              <ActionIcon
                variant="subtle"
                aria-label={t('actions.edit')}
                onClick={onEdit}
                disabled={!row}
              >
                <IconEdit size={14} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label={t('actions.delete')}>
              <ActionIcon
                variant="subtle"
                color="red"
                aria-label={t('actions.delete')}
                onClick={onDeleteRequest}
              >
                <IconTrash size={14} />
              </ActionIcon>
            </Tooltip>
          </>
        ) : null}
      </Group>
    </Group>
  );
}
